import type { TFunction } from "i18next";
import type { IconName } from "../components/icon/Icon";
import type { IconOption } from "../types/Menu";
import type SubformId from "../state/SubformId";
import type { StringTemplate } from "./stringTemplateUtil";

/**
 * We're (or hopefully some day have been) hardcoding urls for default icons.
 * These should be mapped to default icons. Also, http and https are used interchangeably throughout our database.
 * @param url
 * @returns sane icon option
 */
export const fromPinIconUrl = (
  url: string,
): { icon: IconName; color: string; badgeColor: string; hoverColor: string } => {
  switch (url) {
    case "http://cdn.moreapp.com/more-icons/comment_purple.png":
    case "https://cdn.moreapp.com/more-icons/comment_purple.png":
      return {
        icon: "chat",
        color: "text-[#6d28d9]",
        badgeColor: "bg-surface-status-upgrade",
        hoverColor: `group-hover/pin-icon:icon-status-upgrade`,
      };
    case "http://cdn.moreapp.com/more-icons/exclamation_orange.png":
    case "https://cdn.moreapp.com/more-icons/exclamation_orange.png":
      return {
        icon: "exclamation",
        color: "text-[#b45309]",
        badgeColor: "bg-surface-status-warning",
        hoverColor: "group-hover/pin-icon:icon-status-warning",
      };
    case "http://cdn.moreapp.com/more-icons/question_blue.png":
    case "https://cdn.moreapp.com/more-icons/question_blue.png":
      return {
        icon: "questionMarkCircle",
        color: "text-[#2563eb]",
        badgeColor: "bg-surface-status-question",
        hoverColor: "group-hover/pin-icon:icon-status-question",
      };
    case "http://cdn.moreapp.com/more-icons/times_red.png":
    case "https://cdn.moreapp.com/more-icons/times_red.png":
      return {
        icon: "xCircle",
        color: "text-[#dc2626]",
        badgeColor: "bg-surface-status-danger",
        hoverColor: "group-hover/pin-icon:icon-status-danger",
      };
    case "http://cdn.moreapp.com/more-icons/check_green.png":
    case "https://cdn.moreapp.com/more-icons/check_green.png":
    default:
      return {
        icon: "checkCircle",
        color: "text-[#41784c]",
        badgeColor: "bg-surface-status-success",
        hoverColor: "group-hover/pin-icon:icon-status-success",
      };
  }
};

export type PinIcon = { type: "URL" | "RESOURCE"; value: string };
export type Pin = {
  name: string;
  id: string;
  target_form_id?: SubformId | null;
  icon?: PinIcon;
  itemMarkup: StringTemplate;
};

export const getDefaultPinOptions = (t: TFunction): IconOption<PinIcon>[] => [
  {
    variant: "icon",
    label: t(($) => $.FIELD_PROPERTY_PIN_ICON_CHECK),
    value: { type: "URL", value: "https://cdn.moreapp.com/more-icons/check_green.png" },
    icon: { name: "checkCircle", type: "solid", class: "icon-status-success" },
  },
  {
    variant: "icon",
    label: t(($) => $.FIELD_PROPERTY_PIN_ICON_COMMENT),
    value: { type: "URL", value: "https://cdn.moreapp.com/more-icons/comment_purple.png" },
    icon: { name: "chat", type: "solid", class: "icon-status-upgrade" },
  },
  {
    variant: "icon",
    label: t(($) => $.FIELD_PROPERTY_PIN_ICON_EXCLAMATION),
    value: { type: "URL", value: "https://cdn.moreapp.com/more-icons/exclamation_orange.png" },
    icon: { name: "exclamation", type: "solid", class: "icon-status-warning" },
  },
  {
    variant: "icon",
    label: t(($) => $.FIELD_PROPERTY_PIN_ICON_QUESTION),
    value: { type: "URL", value: "https://cdn.moreapp.com/more-icons/question_blue.png" },
    icon: { name: "questionMarkCircle", type: "solid", class: "icon-status-question" },
  },
  {
    variant: "icon",
    label: t(($) => $.FIELD_PROPERTY_PIN_ICON_CROSS),
    value: { type: "URL", value: "https://cdn.moreapp.com/more-icons/times_red.png" },
    icon: { name: "xCircle", type: "solid", class: "icon-status-danger" },
  },
];
